import React, { useCallback, useEffect, useState } from 'react';

import {
  CentroDeCustoConfig,
  ClassificacaoFinanceiraConfig,
  LocalConfig,
  OficinaAliasConfig,
  OficinaExternaOpcao,
  definirExigeOficina,
  definirGrupoDoCentroDeCusto,
  definirGrupoDoLocal,
  fetchConfigCentrosDeCusto,
  fetchConfigClassificacoes,
  fetchConfigLocais,
  fetchConfigOficinas,
  salvarConfigOficinas,
} from '../../../api/comprasDashboardApi';
import { notify } from '../../../shared/feedback/notify';
import SincronizacaoComprasSection from '../components/SincronizacaoComprasSection';
import ComentariosCompraPermissoesSection from '../components/ComentariosCompraPermissoesSection';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import Paginacao from '../../../components/paginacao/Paginacao';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
// at-111: o selo "Confira" do local suspeito. Import explícito — contar com o CSS carregado por
// outra tela é o acoplamento invisível do BUG-V74.
import '../../../shared/ui/badge-status.css';
// O casco das telas (cabeçalho, título, container) é o da Recepção, como nas demais páginas de
// Administração do Sistema — ver ConsumoIaPage e IntegracoesPage.
import '../../recepcao/pages/Recepcao.css';
import './css/adminSistema.css';
import '../../../shared/feedback/aviso-de-tela.css';

/** 152 grafias em produção (medido na at-100): a tabela inteira de uma vez é inutilizável. */
const POR_PAGINA = 25;

/**
 * Administração do Sistema — Compras (at-100, ADR 0050). ADMIN e TI.
 *
 * As configurações existem porque a API do Nasajon não entrega o dado pronto: a oficina vem de
 * texto livre com muitas grafias, e "qual local de estoque é de peça" é decisão de negócio, não do
 * ERP.
 *
 * ⚠️ Havia uma terceira seção, o de-para de nomes de comprador e solicitante. Ela saiu na at-105:
 * o Nasajon documentou a rota de usuários em 15/09/2026, e o nome passou a vir do próprio ERP.
 */

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'Sincronização com o Nasajon',
    conteudo: (
      <>
        <p>
          O painel de Compras não consulta o Nasajon a cada vez que alguém abre a tela: ele lê uma
          cópia local, que o sistema mantém atualizada sozinho. Os dois controles no topo desta tela
          decidem se essas leituras acontecem e com que frequência.
        </p>
        <p>
          <strong>Pausar</strong> interrompe tudo: nenhuma requisição é enviada ao Nasajon, nem nos
          horários automáticos, nem pelo botão de sincronizar agora. Vale imediatamente, sem precisar
          de nova publicação do sistema. O painel continua abrindo com os dados já recebidos e avisa,
          em todas as abas, que está pausado, desde quando e por quem. Nada é apagado — ao retomar, o
          sistema volta a ler a partir daquele momento, sem recuperar o que ficou para trás.
        </p>
        <p>
          O <strong>ritmo das leituras</strong> é escolhido à parte, e fica guardado mesmo durante uma
          pausa: quem retoma não precisa lembrar em que ritmo estava. No ritmo normal, as partes do
          painel são lidas em frequências diferentes ao longo do expediente, e uma mudança no Nasajon
          aparece aqui em até 3 minutos. No ritmo diário, tudo é lido de uma vez no fim do dia — o
          consumo cai muito, e em troca o painel <strong>não muda durante o expediente</strong>: uma
          requisição criada de manhã só aparece à noite.
        </p>
        <p>
          Se o sistema estiver fora do ar no horário da leitura diária, ela <strong>não se perde</strong>:
          acontece assim que ele voltar. E a aba Sincronização do painel mostra o horário em vigor,
          além de quando cada parte foi lida pela última vez.
        </p>
      </>
    ),
  },
  {
    titulo: 'Por que é preciso configurar isto',
    conteudo: (
      <p>
        O Nasajon traz a oficina dentro de um texto livre e escrita de várias formas, e o campo que
        identifica de onde veio a requisição é o <strong>local de estoque</strong>, que na prática
        indica o setor. As duas listas abaixo ensinam o sistema a ler isso.
      </p>
    ),
  },
  {
    titulo: 'E o nome do comprador?',
    conteudo: (
      <p>
        Não se configura mais aqui. O nome do <strong>comprador</strong> e do{' '}
        <strong>solicitante</strong> vem direto do Nasajon, junto com o resto dos cadastros, e é
        atualizado uma vez por semana (e a cada atualização do sistema). Quem entra na empresa aparece sozinho; quem sai continua com o nome nas
        requisições antigas, para o histórico não voltar a mostrar código.
      </p>
    ),
  },
  {
    titulo: 'Oficinas',
    conteudo: (
      <p>
        A mesma oficina costuma aparecer escrita de formas diferentes. Apontar as variações para um
        nome único faz o painel contá-las juntas. O sistema já unifica sozinho diferenças de acento,
        maiúsculas e CNPJ colado no nome — o que ele <strong>não</strong> faz é adivinhar que dois
        nomes parecidos são a mesma empresa, porque errar isso juntaria oficinas distintas sem
        ninguém perceber. Cada grafia também pode ser <strong>ligada a uma oficina do cadastro de
        Oficina Externa</strong>: aí o painel usa o nome de lá, e acompanha quando ele muda.
      </p>
    ),
  },
  {
    titulo: 'Setores',
    conteudo: (
      <>
        <p>
          Marcar um local como <strong>Peças</strong> faz suas requisições entrarem no filtro padrão
          do painel. ⚠️ A mudança vale também para o <strong>passado</strong>: o painel filtra na
          hora da consulta, então requisições antigas daquele local passam a aparecer (ou a sumir)
          imediatamente. Nada é reprocessado porque não há nada gravado por setor.
        </p>
        <p>
          Quando o Nasajon cria um <strong>local de estoque novo</strong>, ele entra aqui como{' '}
          <strong>Outro setor</strong> — a não ser que o nome já diga &quot;peça&quot;, caso em que
          o sistema o classifica sozinho. É o padrão conservador: material de escritório nunca cai
          em Peças por engano.
        </p>
        <p>
          A coluna <strong>Com oficina no texto</strong> conta quantas requisições daquele local
          mencionam uma oficina de destino — o padrão de uma compra de peça. Quando isso acontece
          num local marcado como Outro setor, a linha recebe o selo <strong>Confira</strong>: é o
          sinal de que ele talvez devesse estar em Peças. <strong>É um aviso, não um
          diagnóstico</strong> — quem decide é você, e o selo some assim que o setor daquele local
          for escolhido aqui, mesmo que a escolha seja manter em Outro setor.
        </p>
        <p>
          Por que isso existe: um local de peça classificado como Outro setor tira as requisições
          dele do painel da oficina <strong>sem nada avisar</strong> — não há erro, só um número
          menor do que deveria.
        </p>
      </>
    ),
  },
  {
    titulo: 'Centros de custo',
    conteudo: (
      <>
        <p>
          O filtro principal do painel de Compras é o <strong>centro de custo</strong>, que vem do
          local de uso de cada requisição. O <strong>grupo</strong> de cada centro decide quais
          cards o painel monta: com centros de <strong>Oficina</strong> no filtro, aparecem os
          cards de peça (oficina, placa, entrega); com centros de <strong>Outro setor</strong>,
          aparecem os de solicitante e de itens.
        </p>
        <p>
          Centro novo nasce como Oficina só quando o nome tem &quot;oficina&quot;; os demais nascem
          como Outro setor. Aqui se corrige isso. A mudança vale também para o{' '}
          <strong>passado</strong>: o grupo é lido na hora da consulta, e nada é reprocessado.
        </p>
      </>
    ),
  },
  {
    titulo: 'Comentários em pedidos e requisições',
    conteudo: (
      <>
        <p>
          O detalhe de cada pedido e de cada requisição no Dashboard de Compras tem uma seção de{' '}
          <strong>comentários</strong>, com respostas. Aqui se define quem pode{' '}
          <strong>escrever</strong> e quem pode <strong>responder</strong> — são permissões
          separadas.
        </p>
        <p>
          <strong>Sem nenhum perfil marcado, só os administradores escrevem.</strong> Não existe
          padrão: o que não está marcado está negado. Ler os comentários é de quem abre o Dashboard
          de Compras, e por isso só o perfil de Compras é oferecido aqui.
        </p>
        <p>
          Comentário <strong>não pode ser apagado</strong>. Quem escreveu corrige o texto nos
          primeiros 10 minutos, enquanto ninguém respondeu; depois, a correção é uma resposta. Quem é mencionado com <strong>@</strong> recebe um aviso no
          sino, mesmo sem participar da conversa.
        </p>
      </>
    ),
  },
  {
    titulo: 'Classificações financeiras',
    conteudo: (
      <>
        <p>
          A classificação financeira é como o Nasajon descreve a <strong>natureza do gasto</strong>{' '}
          — &quot;compra de peças oficina externa carro&quot;, &quot;assistência a vidro&quot;,
          &quot;reforma imóvel&quot;. Ela <strong>não diz o nome da oficina</strong>; diz se aquela
          compra <strong>deveria ter</strong> uma oficina de destino.
        </p>
        <p>
          É isso que o card <strong>Requisições por oficina</strong> usa para saber quem ele deve
          contar. Antes desta configuração existir, ele contava todas as requisições do período, e
          a linha &quot;Oficina não informada&quot; juntava duas coisas muito diferentes: a compra
          de peça em que o comprador esqueceu de preencher, e a compra de vidro ou de material de
          escritório, que <strong>nunca teve</strong> oficina nenhuma. Com o universo definido
          aqui, aquela linha passa a ser só a primeira — ou seja, uma lista de requisições que
          alguém pode corrigir.
        </p>
        <p>
          Quando o Nasajon cria uma <strong>classificação nova</strong>, ela entra aqui como{' '}
          <strong>Não</strong>, a não ser que o código dela esteja na lista de compra de peça que a
          área de Compras definiu. O aviso <strong>Confira</strong> aparece quando uma
          classificação não marcada tem, na maioria das requisições, uma oficina informada no texto
          — o padrão de uma compra de peça. Como nos setores, é um aviso e não um diagnóstico: o
          selo some assim que alguém decide a linha, mesmo que a decisão seja manter em Não.
        </p>
        <p>
          No painel, o sinal <strong>*</strong> ao lado de um número indica requisições que estão
          ali <strong>apesar de</strong> a classificação delas não ser de compra de peça — elas
          entram porque têm oficina preenchida, e o sistema não esconde o que o comprador já
          informou. Se o mesmo sinal aparecer sempre nas mesmas linhas, é sinal de que falta marcar
          uma classificação nesta tela.
        </p>
      </>
    ),
  },
];

export default function ComprasConfigPage() {
  const [oficinas, setOficinas] = useState<OficinaAliasConfig[] | null>(null);
  // at-99: as oficinas ativas do cadastro de Oficina Externa. `null` = o backend não mandou (anterior
  // à at-99): a coluna do vínculo não aparece, em vez de oferecer um select vazio.
  const [oficinasExternas, setOficinasExternas] = useState<OficinaExternaOpcao[] | null>(null);
  const [locais, setLocais] = useState<LocalConfig[] | null>(null);
  const [classificacoes, setClassificacoes] = useState<ClassificacaoFinanceiraConfig[] | null>(null);
  const [centros, setCentros] = useState<CentroDeCustoConfig[] | null>(null);
  const [erro, setErro] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [ajudaAberta, setAjudaAberta] = useState(false);
  const [buscaOficina, setBuscaOficina] = useState('');
  const [pagina, setPagina] = useState(1);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [o, l, c, cc] = await Promise.all([
        fetchConfigOficinas(),
        fetchConfigLocais(),
        fetchConfigClassificacoes(),
        fetchConfigCentrosDeCusto(),
      ]);
      setOficinas(o.oficinas);
      setOficinasExternas(o.oficinasExternas ?? null);
      setLocais(l.locais);
      setClassificacoes(c.classificacoes);
      setCentros(cc.centros);
      setErro(false);
    } catch (e) {
      // 🔴 Mantém o estado seguro (null) e avisa: lista vazia aqui seria lida como "não há
      // nada para configurar", e alguém concluiria que o espelho está vazio.
      setOficinas(null);
      setLocais(null);
      setClassificacoes(null);
      setCentros(null);
      setErro(true);
      notify.errorFrom(
        e,
        'Não foi possível carregar as configurações de Compras. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const salvarOficinas = async () => {
    if (!oficinas) return;
    setSalvando(true);
    try {
      await salvarConfigOficinas(
        oficinas.map((o) => ({
          grafia: o.grafia,
          oficinaCanonica: o.oficinaCanonica,
          // Só manda o campo quando a tela o conhece: ausente, o backend preserva o vínculo.
          ...(oficinasExternas ? { oficinaExternaId: o.oficinaExternaId ?? null } : {}),
        })),
      );
      notify.actionSuccess({ action: 'update', subject: 'nome das oficinas' });
      await carregar();
    } catch (e) {
      notify.actionError(e, { action: 'update', subject: 'nome das oficinas' });
    } finally {
      setSalvando(false);
    }
  };

  const trocarGrupo = async (local: LocalConfig, grupo: 'PECAS' | 'OUTROS') => {
    try {
      await definirGrupoDoLocal(local.pk, grupo);
      notify.actionSuccess({ action: 'update', subject: 'setor do local' });
      await carregar();
    } catch (e) {
      notify.actionError(e, { action: 'update', subject: 'setor do local' });
    }
  };

  /*
   * A busca e a paginação existem por medição, não por gosto: são 152 grafias de oficina em
   * produção (at-100). A tabela inteira de uma vez rola indefinidamente e torna impossível achar
   * a variação que se quer unificar — que é a única coisa que se faz nesta tela.
   *
   * ⚠️ A edição é indexada pela GRAFIA, nunca pela posição do `map`: com filtro e paginação, o
   * índice da linha exibida não corresponde ao do array, e editar a linha 3 da página 2 gravaria
   * na 3ª oficina da lista inteira — em silêncio.
   */
  const oficinasFiltradas = (oficinas ?? []).filter(
    (o) =>
      !buscaOficina.trim() ||
      o.grafia.toLowerCase().includes(buscaOficina.trim().toLowerCase()) ||
      o.oficinaCanonica.toLowerCase().includes(buscaOficina.trim().toLowerCase()),
  );
  const totalPaginas = Math.max(1, Math.ceil(oficinasFiltradas.length / POR_PAGINA));
  const paginaSegura = Math.min(pagina, totalPaginas);
  const oficinasDaPagina = oficinasFiltradas.slice(
    (paginaSegura - 1) * POR_PAGINA,
    paginaSegura * POR_PAGINA,
  );

  /**
   * Os locais que o servidor marcou como suspeitos (at-111).
   *
   * ⚠️ Quem decide é o **servidor** (`suspeito`), não a tela: o critério é "em Outro setor, com
   * oficina no texto e sem ninguém ter classificado", e recalculá-lo aqui criaria uma segunda
   * definição do mesmo aviso — que divergiria da do backend no primeiro ajuste.
   */
  const suspeitos = (locais ?? []).filter((l) => l.suspeito);
  const classificacoesSuspeitas = (classificacoes ?? []).filter((c) => c.suspeita);

  const trocarExigeOficina = async (
    classificacao: ClassificacaoFinanceiraConfig,
    exigeOficina: boolean,
  ) => {
    try {
      await definirExigeOficina(classificacao.pk, exigeOficina);
      notify.actionSuccess({ action: 'update', subject: 'tipo da classificação' });
      await carregar();
    } catch (e) {
      notify.actionError(e, { action: 'update', subject: 'tipo da classificação' });
    }
  };

  const trocarGrupoDoCentro = async (centro: CentroDeCustoConfig, grupo: 'PECAS' | 'OUTROS') => {
    try {
      await definirGrupoDoCentroDeCusto(centro.pk, grupo);
      notify.actionSuccess({ action: 'update', subject: 'grupo do centro de custo' });
      await carregar();
    } catch (e) {
      notify.actionError(e, { action: 'update', subject: 'grupo do centro de custo' });
    }
  };

  const editarOficina = (grafia: string, nome: string) =>
    setOficinas((atual) =>
      (atual ?? []).map((o) => (o.grafia === grafia ? { ...o, oficinaCanonica: nome } : o)),
    );

  /*
   * at-99: ligar ao cadastro. 🔴 Indexado pela GRAFIA, nunca pela posição do `map` (com busca e
   * paginação, o índice da linha exibida não é o do array — regra de 16/09/2026). Ligar preenche o
   * nome único com o nome do cadastro; desligar mantém o último nome, para a pessoa editar.
   */
  const ligarOficina = (grafia: string, id: string) =>
    setOficinas((atual) =>
      (atual ?? []).map((o) => {
        if (o.grafia !== grafia) return o;
        if (!id) return { ...o, oficinaExternaId: null, oficinaExternaNome: null, oficinaExternaAtiva: null };
        const externa = oficinasExternas?.find((x) => x.id === id);
        return externa
          ? { ...o, oficinaExternaId: id, oficinaExternaNome: externa.nome, oficinaExternaAtiva: true, oficinaCanonica: externa.nome }
          : o;
      }),
    );

  return (
    <div
      className="Frame1000004012"
      style={{
        height: 'fit-content',
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
      }}
    >
      <div className="ConteDo recepcao-conte-do">
        <div className="recepcao-header-row">
          <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Compras
            <button
              type="button"
              className="help-circle-btn"
              title="Como Funciona"
              onClick={() => setAjudaAberta(true)}
            >
              ?
            </button>
          </div>
        </div>

        {erro ? (
          <p className="erro-de-tela">
            Não foi possível carregar as configurações. Recarregue a página; se persistir, acione a
            TI.
          </p>
        ) : null}

        {/*
          at-108 — os controles do sync vêm PRIMEIRO, e carregam sozinhos.
          Estado próprio de propósito: uma falha ao ler os de-paras não pode esconder o controle de
          pausa, que é o que alguém vem usar quando precisa parar o consumo da API às pressas.
        */}
        <SincronizacaoComprasSection />

        {/* at-129 — quem comenta em pedidos e requisições. Carga própria, como a de cima. */}
        <ComentariosCompraPermissoesSection />

        {carregando ? <p className="admin-hint">Carregando...</p> : null}

        {oficinas ? (
          <section className="admin-card">
            <h2 className="admin-card-title">Oficinas</h2>
            <p className="admin-hint">
              Aponte as variações de escrita para um nome único. O sistema já unifica acento,
              maiúsculas e CNPJ colado no nome; o resto é decisão sua.
            </p>
            {oficinasExternas ? (
              <p className="admin-hint">
                Para usar o nome do <strong>cadastro de Oficina Externa</strong>, escolha a oficina
                na coluna <strong>Oficina do cadastro</strong>: o painel passa a mostrar o nome de
                lá, inclusive nas listas e nos filtros, e acompanha se ele for renomeado. Ligar
                várias grafias à mesma oficina é o que as junta. O trabalho é manual — o sistema não
                adivinha que duas grafias são a mesma empresa. Oficina que não está na lista se
                cadastra em Administração do Sistema → Oficina Externa.
              </p>
            ) : null}

            <div className="admin-input-group">
              <input
                className="admin-input"
                type="search"
                placeholder="Buscar grafia ou nome único"
                value={buscaOficina}
                onChange={(e) => {
                  setBuscaOficina(e.target.value);
                  setPagina(1);
                }}
              />
              <span className="admin-texto-secundario">
                {oficinasFiltradas.length.toLocaleString('pt-BR')} de{' '}
                {oficinas.length.toLocaleString('pt-BR')}
              </span>
            </div>

            {oficinasDaPagina.length === 0 ? (
              <p className="admin-vazio">Nenhuma grafia corresponde à busca.</p>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Como aparece no ERP</th>
                    <th>Requisições</th>
                    {oficinasExternas ? <th>Oficina do cadastro</th> : null}
                    <th>Nome único</th>
                  </tr>
                </thead>
                <tbody>
                  {oficinasDaPagina.map((o) => (
                    <tr key={o.grafia}>
                      <td title={o.grafia}>{o.grafia}</td>
                      <td>{o.ocorrencias.toLocaleString('pt-BR')}</td>
                      {oficinasExternas ? (
                        <td>
                          <select
                            className="admin-select"
                            value={o.oficinaExternaId ?? ''}
                            aria-label={`Oficina do cadastro de ${o.grafia}`}
                            onChange={(e) => ligarOficina(o.grafia, e.target.value)}
                          >
                            <option value="">— não ligada —</option>
                            {/* A oficina desativada não está nas opções, mas o vínculo continua
                                valendo e precisa aparecer selecionado. */}
                            {o.oficinaExternaId && !oficinasExternas.some((x) => x.id === o.oficinaExternaId) ? (
                              <option value={o.oficinaExternaId}>
                                {o.oficinaExternaNome ?? o.oficinaCanonica} (inativa)
                              </option>
                            ) : null}
                            {oficinasExternas.map((x) => (
                              <option key={x.id} value={x.id}>
                                {x.regiao ? `${x.nome} - ${x.regiao}` : x.nome}
                              </option>
                            ))}
                          </select>{' '}
                          {o.oficinaExternaId && o.oficinaExternaAtiva === false ? (
                            <span className="cilia-badge badge-cinza">inativa</span>
                          ) : null}
                        </td>
                      ) : null}
                      <td>
                        <input
                          className="admin-input"
                          value={o.oficinaCanonica}
                          aria-label={`Nome único de ${o.grafia}`}
                          // Ligada ao cadastro, o nome vem de lá: editar aqui não teria efeito.
                          disabled={Boolean(o.oficinaExternaId)}
                          title={o.oficinaExternaId ? 'Nome do cadastro de Oficina Externa' : undefined}
                          onChange={(e) => editarOficina(o.grafia, e.target.value)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {totalPaginas > 1 ? (
              <Paginacao
                paginaAtual={paginaSegura}
                totalPaginas={totalPaginas}
                onMudarPagina={setPagina}
              />
            ) : null}

            {/*
              ⚠️ O salvamento é DECLARATIVO e manda a lista inteira, não só a página visível: quem
              editou na página 1 e salvou na 3 não pode perder a edição da 1.
            */}
            <button type="button" className="admin-btn" disabled={salvando} onClick={salvarOficinas}>
              Salvar oficinas
            </button>
          </section>
        ) : null}

        {locais ? (
          <section className="admin-card">
            <h2 className="admin-card-title">Setores</h2>
            <p className="admin-hint">
              Marque quais locais de estoque são de peças. O painel abre filtrado neles. A mudança
              vale também para as requisições antigas daquele local.
            </p>

            {/*
              🔴 at-111 — o sinal que faltava.
              Um local de peça classificado como "Outro setor" tira as requisições dele do painel da
              oficina sem nada avisar: não há erro, só um número menor do que deveria. Foi assim que
              `PECA CARRO` levou 13 requisições para fora, e a descoberta veio de alguém estranhar um
              número, não do sistema.
            */}
            {suspeitos.length > 0 ? (
              <p className="aviso-de-tela">
                {suspeitos.length === 1
                  ? '1 local em "Outro setor" tem requisições que mencionam uma oficina de destino'
                  : `${suspeitos.length} locais em "Outro setor" têm requisições que mencionam uma oficina de destino`}{' '}
                — o padrão de uma compra de peça. Confira se deveriam estar em Peças. Local novo
                nasce em &quot;Outro setor&quot;, e o aviso some quando alguém escolhe o setor aqui.
              </p>
            ) : null}

            <table className="admin-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Local</th>
                  <th>Requisições</th>
                  <th>Com oficina no texto</th>
                  <th>Setor</th>
                </tr>
              </thead>
              <tbody>
                {locais.map((l) => (
                  <tr key={l.pk}>
                    <td>{l.codigo ?? '—'}</td>
                    <td title={l.descricao ?? undefined}>
                      {/* Célula é UMA linha: o selo vai ao lado do nome, nunca abaixo dele. */}
                      {l.descricao ?? '—'}{' '}
                      {l.suspeito ? (
                        <span
                          className="cilia-badge badge-laranja"
                          title="Este local está em Outro setor e tem requisições com oficina de destino no texto. Confira se ele não é de peças."
                        >
                          Confira
                        </span>
                      ) : null}
                    </td>
                    <td>{l.requisicoes.toLocaleString('pt-BR')}</td>
                    <td>{l.comOficina.toLocaleString('pt-BR')}</td>
                    <td>
                      <div className="select-wrapper">
                        <select
                          value={l.grupo}
                          aria-label={`Setor de ${l.descricao ?? l.codigo ?? l.pk}`}
                          onChange={(e) => void trocarGrupo(l, e.target.value as 'PECAS' | 'OUTROS')}
                        >
                          <option value="PECAS">Peças</option>
                          <option value="OUTROS">Outro setor</option>
                        </select>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ) : null}

        {centros ? (
          <section className="admin-card">
            <h2 className="admin-card-title">Centros de custo</h2>
            <p className="admin-hint">
              O grupo decide quais cards o painel de Compras monta: os de oficina (oficina, placa,
              entrega) ou os de outros setores (solicitante, itens). A mudança vale também para as
              requisições antigas.
            </p>

            {centros.length === 0 ? (
              <p className="admin-vazio">
                Nenhum centro de custo sincronizado ainda. Eles chegam com os cadastros do Nasajon.
              </p>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Centro de custo</th>
                    <th>Requisições</th>
                    <th>Grupo</th>
                  </tr>
                </thead>
                <tbody>
                  {centros.map((c) => (
                    <tr key={c.pk}>
                      <td>{c.codigo ?? '—'}</td>
                      <td title={c.descricao ?? undefined}>{c.descricao ?? '—'}</td>
                      <td>{c.requisicoes.toLocaleString('pt-BR')}</td>
                      <td>
                        <div className="select-wrapper">
                          <select
                            value={c.grupo}
                            aria-label={`Grupo de ${c.descricao ?? c.codigo ?? c.pk}`}
                            onChange={(e) => void trocarGrupoDoCentro(c, e.target.value as 'PECAS' | 'OUTROS')}
                          >
                            <option value="PECAS">Oficina</option>
                            <option value="OUTROS">Outro setor</option>
                          </select>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        ) : null}

        {classificacoes ? (
          <section className="admin-card">
            <h2 className="admin-card-title">Classificações financeiras</h2>
            <p className="admin-hint">
              Marque quais classificações são de compra de peça automotiva. São elas que definem
              quais requisições entram no card &quot;Requisições por oficina&quot; do painel — e,
              por consequência, o que significa a linha &quot;Oficina não informada&quot;: uma
              compra de peça em que o comprador não informou a oficina de destino. A mudança vale
              também para as requisições antigas.
            </p>

            {/*
              🔴 O mesmo sinal que a seção de Setores tem, pelo mesmo motivo (at-111): uma
              classificação de peça não marcada tira as requisições dela do card sem nada avisar —
              não há erro, só um número menor do que deveria. Classificação nova criada no ERP
              nasce desmarcada, e é este aviso que a denuncia.
            */}
            {classificacoesSuspeitas.length > 0 ? (
              <p className="aviso-de-tela">
                {classificacoesSuspeitas.length === 1
                  ? '1 classificação não marcada tem, na maioria das requisições, uma oficina de destino informada'
                  : `${classificacoesSuspeitas.length} classificações não marcadas têm, na maioria das requisições, uma oficina de destino informada`}{' '}
                — o padrão de uma compra de peça. Confira se deveriam ser marcadas. Classificação
                nova nasce desmarcada, e o aviso some quando alguém decide aqui.
              </p>
            ) : null}

            <table className="admin-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Classificação</th>
                  <th>Requisições</th>
                  <th>Com oficina no texto</th>
                  <th>É compra de peça?</th>
                </tr>
              </thead>
              <tbody>
                {classificacoes.map((c) => (
                  <tr key={c.pk}>
                    <td>{c.codigo ?? '—'}</td>
                    <td title={c.descricao ?? undefined}>
                      {/* Célula é UMA linha: o selo vai ao lado do nome, nunca abaixo dele. */}
                      {c.descricao ?? '—'}{' '}
                      {c.suspeita ? (
                        <span
                          className="cilia-badge badge-laranja"
                          title="A maioria das requisições desta classificação traz oficina de destino no texto. Confira se ela não é de compra de peça."
                        >
                          Confira
                        </span>
                      ) : null}
                    </td>
                    <td>{c.requisicoes.toLocaleString('pt-BR')}</td>
                    <td>{c.comOficina.toLocaleString('pt-BR')}</td>
                    <td>
                      <div className="select-wrapper">
                        <select
                          value={c.exigeOficina ? 'SIM' : 'NAO'}
                          aria-label={`Compra de peça: ${c.descricao ?? c.codigo ?? c.pk}`}
                          onChange={(e) => void trocarExigeOficina(c, e.target.value === 'SIM')}
                        >
                          <option value="SIM">Sim, exige oficina</option>
                          <option value="NAO">Não</option>
                        </select>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ) : null}

        <ComoFunciona
          isOpen={ajudaAberta}
          onClose={() => setAjudaAberta(false)}
          titulo="Como Funciona - Configuração de Compras"
          secoes={helpSections}
        />
      </div>
    </div>
  );
}
